/**
 * Iter 122.trial-pivot — Calm topbar trial pill.
 *
 * Shows three states:
 *   1. **Trial · N days left** — default for trial accounts. Soft
 *      teal pill that fades up on render.
 *   2. **Upgrade to download** — morphs when the user opens any
 *      export panel, hovers a download button, or hits a 402 with
 *      `scope === "export"`. The export-intent helper drives this
 *      state via a windowed CustomEvent; we never poll.
 *   3. **Read-only · Upgrade to add records** — post-trial free
 *      accounts. Calm wording, no data-loss implication.
 *
 * Hidden for accounts that don't need to see it (admin, paid,
 * complimentary, third-party, locked) — the topbar already has
 * enough chrome.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Sparkles, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useExportIntentEvent } from "@/lib/exportIntent";

export default function TrialPill({ compact = false, testid = "trial-pill" }) {
  const { user } = useAuth();
  const plan = user?.plan;
  const [intent, setIntent] = useState(() =>
    (typeof window !== "undefined" && window.__cpcExportIntent) || { active: false, feature: null },
  );
  useExportIntentEvent(setIntent);

  // Mount-fade so the pill enters calmly rather than popping in.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 50); return () => clearTimeout(t); }, []);

  const view = useMemo(() => {
    if (!plan) return null;
    const isTrial = plan.plan === "trial";
    const isFree  = plan.plan === "free";
    // Anyone with full export rights never needs the pill — they
    // already have the unlocked behaviour.
    if (!isTrial && !isFree) return null;

    if (intent.active) {
      // Override state — morph to the upgrade affordance regardless
      // of trial vs free. Same call to action: subscribe.
      return {
        key: "upgrade-to-download",
        Icon: Download,
        label: intent.feature ? `Upgrade to download · ${intent.feature}` : "Upgrade to download",
        tone: "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100",
        title: "Downloads & exports are unlocked on any paid plan. Your work stays here until you decide.",
        to: "/app/upgrade",
      };
    }
    if (isTrial) {
      const left = plan.trial_days_remaining;
      const days = (left == null) ? "active" : (left === 1 ? "1 day left" : `${left} days left`);
      return {
        key: "trial",
        Icon: Sparkles,
        label: `Trial · ${days}`,
        tone: "bg-[#D6F0EF] text-[#0F5658] border-[#A7D8DA] hover:bg-[#C8E8E7]",
        title: "Full access during your 30-day free trial. Downloads unlock with any paid plan.",
        to: "/app/upgrade",
      };
    }
    // free
    return {
      key: "free",
      Icon: Lock,
      label: compact ? "Read-only · Upgrade" : "Read-only · Upgrade to add records",
      tone: "bg-[#FBF0DC] text-[#6F4E1E] border-[#E5C8A6] hover:bg-[#FAE6C8]",
      title: "Your records remain safe and available to view. Upgrade to record new information and unlock downloads.",
      to: "/app/upgrade",
    };
  }, [plan, intent, compact]);

  if (!view) return null;

  const Icon = view.Icon;
  return (
    <Link
      to={view.to}
      title={view.title}
      data-testid={testid}
      data-state={view.key}
      aria-label={view.label}
      className={`group inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2.5 py-1 transition-all duration-300 ${view.tone} ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-0.5"
      }`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.8} />
      <span className="truncate max-w-[200px] sm:max-w-[260px]">{view.label}</span>
    </Link>
  );
}
